// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import { gql, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

const diferenciaDias = (fechaDesde, fechaHasta) => {
  const fecha1 = dayjs(fechaDesde);
  const fecha2 = dayjs(fechaHasta);
  const resultado = fecha2.diff(fecha1, "days");
  return resultado + 1;
};

export default function ModalAdd({ open, handleClose, refetch, selectedPermiso, tipo }) {
  const [permiso, setPermiso] = useState();
  const inputRef = useRef();
  const [file, setFile] = useState();
  const { handleSnackbar } = useContext(MessageManager);

  useEffect(() => {
    if (!open) {
      setPermiso({
        fechaDesde: dayjs().format("YYYY-MM-DD"),
        fechaHasta: dayjs().format("YYYY-MM-DD"),
      });
    }
  }, [open]);

  const importeAbonado =
    permiso?.tipo === "Operador cinegético"
      ? permiso?.valorPermisoTemporada * diferenciaDias(permiso?.fechaDesde, permiso?.fechaHasta)
      : permiso?.valorPermisoTemporada || 0;

  const handleChanges = (e) => {
    setPermiso({
      ...permiso,
      [e.target.name]: e.target.value,
    });
  };

  const [saveUsuario, { loading: loadingSave }] = useMutation(
    gql`
      mutation addPermisoOcCoto($input: PermisoOcCotoInput!) {
        addPermisoOcCoto(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    saveUsuario({
      variables: {
        input: {
          ...permiso,
          file: file,
          fechaDesde: permiso?.tipo === "Coto" ? dayjs().format("YYYY-MM-DD") : permiso?.fechaDesde,
          fechaHasta:
            permiso?.tipo === "Coto" ? dayjs().format("YYYY") + "-12-31" : permiso?.fechaHasta,
          importeAbonado: importeAbonado,
          anioPermiso: Number(dayjs().format("YYYY")),
        },
      },
    })
      .then(() => {
        refetch();
        handleClose();
        handleSnackbar("Permiso guardado correctamente");
      })
      .catch((e) => {
        handleSnackbar("Error al guardar el permiso", "error");
      });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedPermiso ? `Editar permiso` : `Agregar permiso`}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Tipo de permiso
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <Select name="tipo" value={permiso?.tipo || -1} onChange={handleChanges}>
                    <MenuItem value={-1} disabled>
                      Seleccione uno
                    </MenuItem>
                    <MenuItem value={"Coto"}>Coto</MenuItem>
                    <MenuItem value={"Operador cinegético"}>Operador cinegético</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="personaHabilitada">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nombre completo persona habilitada
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="personaHabilitada"
                    placeholder="Ingrese nombre completo del permiso"
                    type="text"
                    name="personaHabilitada"
                    value={permiso?.personaHabilitada || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="dni">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      DNI
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="dni"
                    placeholder="Ingrese el dni del permiso"
                    type="text"
                    name="dni"
                    value={permiso?.dni || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="domicilio">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Domicilio
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="domicilio"
                    type="text"
                    name="domicilio"
                    value={permiso?.domicilio || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                {permiso?.tipo !== "Coto" ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="fechaDesde">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha inicio validez
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="fechaDesde"
                        type="date"
                        name="fechaDesde"
                        value={permiso?.fechaDesde || ""}
                        onChange={handleChanges}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InputLabel htmlFor="fechaHasta">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha fin validez
                        </SoftTypography>
                      </InputLabel>
                      <SoftInput
                        label="fechaHasta"
                        type="date"
                        name="fechaHasta"
                        value={permiso?.fechaHasta || ""}
                        onChange={handleChanges}
                        error={
                          permiso?.fechaDesde &&
                          permiso?.fechaHasta &&
                          dayjs(permiso?.fechaDesde).isAfter(dayjs(permiso?.fechaHasta))
                        }
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} sm={12}>
                    <InputLabel htmlFor="fechaHasta">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Fecha fin validez
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="fechaHasta"
                      type="date"
                      name="fechaHasta"
                      //add 1 year
                      value={dayjs().format("YYYY") + "-12-31"}
                      disabled
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="valorPermisoTemporada">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Valor permiso
                    </SoftTypography>
                  </InputLabel>
                  <SoftInputNumber
                    label="valorPermisoTemporada"
                    name="valorPermisoTemporada"
                    value={permiso?.valorPermisoTemporada || ""}
                    onChange={handleChanges}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel htmlFor="importeAbonado">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Importe abonado
                    </SoftTypography>
                  </InputLabel>
                  <SoftInputNumber
                    label="importeAbonado"
                    name="importeAbonado"
                    value={importeAbonado}
                    onChange={handleChanges}
                    disabled
                  />
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="flex-end"
              alignItems="center"
            >
              {/* <Grid
                display={{
                  xs: "block",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftButton
                  color={file ? "success" : "gobSecondary"}
                  fullWidth={{
                    xs: true,
                    sm: false,
                  }}
                  onClick={() => inputRef.current.click()}
                >
                  {file
                    ? file.name.length > 40
                      ? `${file.name.substring(0, 40)}...${file.name.substring(
                          file.name.length - 4
                        )}`
                      : file.name
                    : "Subir comprobante de pago"}
                  &nbsp;
                  <Icon>upload</Icon>
                </SoftButton>
              </Grid> */}
              <SoftBox
                display={{
                  xs: "flex-row",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftBox
                  mr={{
                    xs: 0,
                    sm: 2,
                  }}
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="primary"
                    onClick={handleClose}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                  >
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="gobSecondary"
                    onClick={handleSave}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                    disabled={
                      loadingSave ||
                      !permiso?.personaHabilitada ||
                      !permiso?.dni ||
                      !permiso?.tipo ||
                      !permiso?.valorPermisoTemporada ||
                      (permiso?.tipo !== "Coto" &&
                        (permiso.fechaDesde > permiso.fechaHasta ||
                          !permiso.fechaDesde ||
                          !permiso.fechaHasta))
                    }
                  >
                    {loadingSave ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          marginRight: 0.5,
                        }}
                        color="inherit"
                      />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <input
              ref={inputRef}
              type="file"
              style={{ display: "none" }}
              accept=".pdf, .jpg, .jpeg, .png"
              onChange={(e) => {
                if (e.target.files[0] && e.target.files[0].size < 5000000) {
                  setFile(e.target.files[0]);
                } else {
                  handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                }
              }}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAdd.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedPermiso: PropTypes.object,
  tipo: PropTypes.string,
};
